import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { usePortfolios } from '../hooks/usePortfolios'
import type { Portfolio, PortfolioContent } from '../hooks/usePortfolios'
import { post } from '../lib/api'
import {
  useConfirm,
  useToast,
  StudioLayout,
  Modal,
} from '../components/ui'
import { gettext as _ } from '../lib/i18n'
import styles from './PortfoliosPage.module.css'

/* ─── types ─── */

const FRAMEWORKS = ['ATS', 'Toss 5줄', 'Amazon LP', 'Google 6축', 'STAR', 'P→S→E→R'] as const

interface PortfolioControls {
  packagingLevel: number
  frameworks: string[]
  forbiddenWords: string
  requiredKeywords: string
}

const DEFAULT_CONTROLS: PortfolioControls = {
  packagingLevel: 50,
  frameworks: ['STAR'],
  forbiddenWords: '',
  requiredKeywords: '',
}

/* ─── helpers ─── */

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}.${m}.${day}`
}

/* ─── Center: portfolio paper (pure presentation) ─── */

function PortfolioPaper({ content }: { content: PortfolioContent }) {
  const sections = content.sections ?? []
  return (
    <article className={styles.portfolioPaper}>
      {content.title && <h1 className={styles.paperTitle}>{content.title}</h1>}

      {content.summary && (
        <div className={styles.summaryBlock}>
          <p className={styles.summaryText}>{content.summary}</p>
        </div>
      )}

      {sections.length > 0 && (
        <section className={styles.projects}>
          <h2 className={styles.projectsTitle}>{_('Projects')}</h2>
          {sections.map((proj, i) => {
            const meta = [proj.role, proj.period].filter(Boolean).join(' · ')
            const tech = proj.techStack ?? []
            const outcomes = proj.outcomes ?? []
            const bullets = proj.bullet_points ?? []
            return (
              <div key={`${proj.heading}-${i}`} className={styles.projectCard}>
                <div className={styles.projectHeader}>
                  <span className={styles.projectName}>{proj.heading}</span>
                  {meta && <span className={styles.projectMeta}>{meta}</span>}
                </div>

                {proj.body && <p className={styles.projectBody}>{proj.body}</p>}

                {tech.length > 0 && (
                  <div className={styles.projectTech}>
                    {tech.map((t, j) => (
                      <span key={`${t}-${j}`} className={styles.techChip}>
                        {t}
                      </span>
                    ))}
                  </div>
                )}

                {outcomes.length > 0 && (
                  <ul className={styles.projectOutcomes}>
                    {outcomes.map((o, j) => (
                      <li key={`${o}-${j}`} className={styles.outcomeItem}>
                        <span className={styles.outcomeCheck}>✓</span>
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {bullets.length > 0 && (
                  <ul className={styles.projectBullets}>
                    {bullets.map((b, j) => (
                      <li key={`${b}-${j}`}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })}
        </section>
      )}
    </article>
  )
}

/* ─── Left rail: portfolio selector ─── */

function PortfolioSelector({
  portfolios,
  selectedId,
  onSelect,
}: {
  portfolios: Portfolio[]
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  return (
    <div className={styles.selector}>
      <div className={styles.railLabel}>{_('Portfolios')}</div>
      {portfolios.length === 0 ? (
        <div className={styles.selectorEmpty}>{_('No portfolios yet')}</div>
      ) : (
        <ul className={styles.selectorList}>
          {portfolios.map((p) => {
            const title = p.content?.title || _('AI-generated portfolio')
            return (
              <li key={p.id}>
                <button
                  type="button"
                  className={`${styles.selectorItem} ${
                    selectedId === p.id ? styles.selectorItemActive : ''
                  }`}
                  onClick={() => onSelect(p.id)}
                >
                  <span className={styles.selectorItemTitle}>{title}</span>
                  <span className={styles.selectorDate}>
                    {formatDate(p.createdAt)}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

/* ─── Right rail: AI control panel ─── */

function AIControlPanel({
  talentId,
  generating,
  onGenerate,
  notionDisabled,
  onPushNotion,
  pushing,
  onDelete,
  deleting,
  canDelete,
  counselingMode,
  controls,
  onControlsChange,
  onImported,
}: {
  talentId: string | undefined
  generating: boolean
  onGenerate: () => void
  notionDisabled: boolean
  onPushNotion: () => void
  pushing: boolean
  onDelete: () => void
  deleting: boolean
  canDelete: boolean
  counselingMode: boolean
  controls: PortfolioControls
  onControlsChange: (c: PortfolioControls) => void
  onImported: (url: string) => void
}) {
  const [importOpen, setImportOpen] = useState(false)
  const [importUrl, setImportUrl] = useState('')
  const [importing, setImporting] = useState(false)
  const toast = useToast()

  const handleImport = async () => {
    if (!talentId || !importUrl.trim()) return
    if (!importUrl.includes('notion.')) {
      toast.error(_('Invalid URL'))
      return
    }
    setImporting(true)
    try {
      await post(`/api/v1/talents/${talentId}/notion/import-public`, {
        url: importUrl.trim(),
        kind: 'portfolio',
      })
      toast.success(_('Notion portfolio imported — AI will counsel-improve it'))
      setImportOpen(false)
      onImported(importUrl.trim())
      setImportUrl('')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : _('Import failed'))
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className={styles.aiPanel}>
      <div className={styles.privateHeader}>
        <span className={styles.privateLock}>🔒</span>
        {_('Private panel')}
      </div>

      {counselingMode && (
        <div className={styles.counselingBadge}>
          🗣️ {_('Counseling mode — existing portfolio detected')}
        </div>
      )}

      {/* ━━━ Professional controls ━━━ */}

      {/* Packaging intensity slider */}
      <div className={styles.controlField}>
        <div className={styles.controlLabel}>{_('Packaging intensity')}</div>
        <div className={styles.sliderWrap}>
          <div className={styles.sliderValue}>{controls.packagingLevel}</div>
          <input
            type="range"
            min="0"
            max="100"
            value={controls.packagingLevel}
            onChange={(e) =>
              onControlsChange({ ...controls, packagingLevel: Number(e.target.value) })
            }
            className={styles.packSlider}
          />
          <div className={styles.sliderLabels}>
            <span>{_('Fact')}</span>
            <span>{_('Emphasis')}</span>
          </div>
        </div>
        <div
          className={styles.packWarn}
          style={
            controls.packagingLevel >= 90
              ? { color: 'var(--amber)' }
              : undefined
          }
        >
          {controls.packagingLevel >= 90
            ? _('Max emphasis — risk of distortion')
            : controls.packagingLevel >= 70
              ? _('High intensity — review recommended')
              : _('No distortion — highlights strengths')}
        </div>
      </div>

      {/* Framework chips */}
      <div className={styles.controlField}>
        <div className={styles.controlLabel}>{_('Portfolio framework')}</div>
        <div className={styles.frameworkChips}>
          {FRAMEWORKS.map((fw) => {
            const selected = controls.frameworks.includes(fw)
            return (
              <button
                key={fw}
                type="button"
                className={`${styles.fwChip} ${selected ? styles.fwChipSelected : ''}`}
                onClick={() =>
                  onControlsChange({
                    ...controls,
                    frameworks: selected
                      ? controls.frameworks.filter((f) => f !== fw)
                      : [...controls.frameworks, fw],
                  })
                }
              >
                {selected && <span className={styles.fwCheck}>✓</span>}
                {fw}
              </button>
            )
          })}
        </div>
      </div>

      {/* Forbidden words */}
      <div className={styles.controlField}>
        <div className={styles.controlLabel}>{_('Forbidden words')}</div>
        <input
          type="text"
          className={styles.controlInput}
          value={controls.forbiddenWords}
          onChange={(e) =>
            onControlsChange({ ...controls, forbiddenWords: e.target.value })
          }
          placeholder={_('e.g. synergy, passion, disruption')}
        />
      </div>

      {/* Required keywords */}
      <div className={styles.controlField}>
        <div className={styles.controlLabel}>{_('Required keywords')}</div>
        <input
          type="text"
          className={styles.controlInput}
          value={controls.requiredKeywords}
          onChange={(e) =>
            onControlsChange({ ...controls, requiredKeywords: e.target.value })
          }
          placeholder={_('JD keywords to include')}
        />
      </div>

      <div className={styles.controlNote}>
        {_('These settings are private — not exposed in output')}
      </div>

      <div className={styles.controlDivider} />

      <button
        type="button"
        className={styles.importNotionBtn}
        onClick={() => setImportOpen(true)}
        disabled={!talentId}
      >
        📥 {_('Import from Notion')}
      </button>

      <Modal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title={_('Import by URL')}
        size="md"
      >
        <div className={styles.urlImportWrap}>
          <p className={styles.urlImportHint}>{_('Enter public Notion URL')}</p>
          <input
            type="url"
            className={styles.controlInput}
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            placeholder="https://workspace.notion.site/..."
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleImport()
            }}
          />
          <button
            type="button"
            className={styles.importNotionBtn}
            onClick={handleImport}
            disabled={importing || !importUrl.trim()}
            style={{ marginTop: 12 }}
          >
            {importing ? _('Importing...') : _('Import by URL')}
          </button>
        </div>
      </Modal>

      <button
        type="button"
        className={styles.magicBtn}
        onClick={onGenerate}
        disabled={generating}
      >
        {generating ? (
          <>
            <span className={styles.spinner} /> {_('Generating...')}
          </>
        ) : (
          <>
            <span className={styles.sparkle}>✨</span> {_('Generate with AI')}
          </>
        )}
      </button>

      <div className={styles.aiDivider} />

      <div className={styles.aiField}>
        <div className={styles.aiFieldLabel}>{_('Export')}</div>
        <button
          type="button"
          className={styles.notionBtn}
          onClick={onPushNotion}
          disabled={notionDisabled || pushing}
        >
          {pushing ? _('Pushing to Notion...') : '📄 ' + _('Write to Notion')}
        </button>
        {talentId && (
          <Link
            to={`/talents/${talentId}/notion`}
            className={styles.aiHint}
          >
            {_('Manage Notion connection')}
          </Link>
        )}
      </div>

      {canDelete && (
        <button
          type="button"
          className={styles.deleteBtn}
          onClick={onDelete}
          disabled={deleting}
        >
          {deleting ? _('Deleting...') : '🗑 ' + _('Delete portfolio')}
        </button>
      )}
    </div>
  )
}

/* ─── Page ─── */

export default function PortfoliosPage() {
  const { talentId } = useParams<{ talentId: string }>()
  const {
    portfolios,
    loading,
    error,
    reload,
    generate,
    generating,
    pushNotion,
    remove,
  } = usePortfolios(talentId)
  const toast = useToast()
  const { confirm } = useConfirm()

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [pushing, setPushing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [controls, setControls] = useState<PortfolioControls>(DEFAULT_CONTROLS)
  const [liveHtml, setLiveHtml] = useState<string | null>(null)
  const [liveUrl, setLiveUrl] = useState<string | null>(null)
  const [liveLoading, setLiveLoading] = useState(false)
  const [emptyImportOpen, setEmptyImportOpen] = useState(false)
  const [emptyImportUrl, setEmptyImportUrl] = useState('')
  const [emptyImporting, setEmptyImporting] = useState(false)

  // Keep a valid portfolio selected (auto-select first/newest).
  useEffect(() => {
    if (portfolios.length === 0) {
      if (selectedId !== null) setSelectedId(null)
      return
    }
    const exists = portfolios.some((p) => p.id === selectedId)
    if (!exists) {
      // Backend returns DESC by created_at, so [0] is already the newest.
      setSelectedId(portfolios[0]?.id ?? null)
    }
  }, [portfolios, selectedId])

  const selected =
    portfolios.find((p) => p.id === selectedId) ?? null

  // Fetch live Notion HTML after URL import (mirrors Resume Studio pattern).
  const handleImported = useCallback(async (url: string) => {
    if (!talentId) return
    setLiveUrl(url)
    setLiveLoading(true)
    try {
      const result = await post<{ html: string; url: string; title: string }>(
        `/api/v1/talents/${talentId}/notion/render-public`,
        { url },
      )
      setLiveHtml(result.html)
    } catch {
      setLiveHtml(null)
    } finally {
      setLiveLoading(false)
    }
  }, [talentId])

  // Empty-state URL import: POST import-public then fetch live HTML.
  const handleEmptyImport = async () => {
    if (!talentId || !emptyImportUrl.trim()) return
    if (!emptyImportUrl.includes('notion.')) {
      toast.error(_('Invalid URL'))
      return
    }
    setEmptyImporting(true)
    try {
      await post(`/api/v1/talents/${talentId}/notion/import-public`, {
        url: emptyImportUrl.trim(),
        kind: 'portfolio',
      })
      toast.success(_('Notion portfolio imported — AI will counsel-improve it'))
      setEmptyImportOpen(false)
      const url = emptyImportUrl.trim()
      setEmptyImportUrl('')
      await handleImported(url)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : _('Import failed'))
    } finally {
      setEmptyImporting(false)
    }
  }

  const handleGenerate = async () => {
    try {
      await generate({
        packagingLevel: controls.packagingLevel,
        frameworks: controls.frameworks,
        forbiddenWords: controls.forbiddenWords
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        requiredKeywords: controls.requiredKeywords
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      })
      toast.success(_('Portfolio generated'))
    } catch (e) {
      toast.error(e instanceof Error ? e.message : _('AI generation failed'))
    }
  }

  const handlePushNotion = async () => {
    if (!selected) return
    setPushing(true)
    try {
      await pushNotion(selected.id)
      toast.success(_('Pushed to Notion'))
      await reload()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : _('Notion push failed'))
    } finally {
      setPushing(false)
    }
  }

  const handleDelete = async () => {
    if (!selected) return
    const ok = await confirm({
      message: _('Delete this portfolio?'),
      danger: true,
    })
    if (!ok) return
    setDeleting(true)
    try {
      await remove(selected.id)
      toast.success(_('Portfolio deleted'))
    } catch (e) {
      toast.error(e instanceof Error ? e.message : _('Delete failed'))
    } finally {
      setDeleting(false)
    }
  }

  /* ─── Loading state ─── */
  if (loading) {
    return (
      <div className={styles.pageRoot}>
        <div className={styles.stateWrap}>
          <div className={styles.loadingWrap}>
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className={styles.skeleton} />
            ))}
          </div>
        </div>
      </div>
    )
  }

  /* ─── Error state ─── */
  if (error) {
    return (
      <div className={styles.pageRoot}>
        <div className={styles.stateWrap}>
          <div className={styles.errorWrap}>
            <span className={styles.errorMsg}>{error}</span>
            <button className={styles.retryBtn} onClick={reload}>
              {_('Retry')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  /* ─── Empty state (outside StudioLayout) ─── */
  if (portfolios.length === 0 && !liveHtml && !liveLoading) {
    return (
      <div className={styles.pageRoot}>
        <div className={styles.stateWrap}>
          <div className={styles.emptyWrap}>
            <div className={styles.emptyGenerate}>
              <div className={styles.emptyIcon}>🗂️</div>
              <div className={styles.emptyText}>
                {_('Generate first portfolio with AI')}
              </div>
              <button
                type="button"
                className={styles.magicBtn}
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating ? (
                  <>
                    <span className={styles.spinner} /> {_('Generating...')}
                  </>
                ) : (
                  <>
                    <span className={styles.sparkle}>✨</span>{' '}
                    {_('Write with AI')}
                  </>
                )}
              </button>
              <button
                type="button"
                className={styles.importNotionBtn}
                onClick={() => setEmptyImportOpen(true)}
                disabled={!talentId}
              >
                📥 {_('Import from Notion')}
              </button>
              {talentId && (
                <Link
                  to={`/talents/${talentId}/sources`}
                  className={styles.aiWarnLink}
                >
                  ⚠ {_('Upload sources first to generate a portfolio')}
                </Link>
              )}
            </div>
          </div>
        </div>

        <Modal
          open={emptyImportOpen}
          onClose={() => setEmptyImportOpen(false)}
          title={_('Import by URL')}
          size="md"
        >
          <div className={styles.urlImportWrap}>
            <p className={styles.urlImportHint}>{_('Enter public Notion URL')}</p>
            <input
              type="url"
              className={styles.controlInput}
              value={emptyImportUrl}
              onChange={(e) => setEmptyImportUrl(e.target.value)}
              placeholder="https://workspace.notion.site/..."
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleEmptyImport()
              }}
            />
            <button
              type="button"
              className={styles.importNotionBtn}
              onClick={handleEmptyImport}
              disabled={emptyImporting || !emptyImportUrl.trim()}
              style={{ marginTop: 12 }}
            >
              {emptyImporting ? _('Importing...') : _('Import by URL')}
            </button>
          </div>
        </Modal>
      </div>
    )
  }

  /* ─── Studio workspace (3-column) ─── */
  return (
    <div className={styles.pageRoot}>
      <div className={styles.studioWrap}>
        <StudioLayout
          left={
            <PortfolioSelector
              portfolios={portfolios}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          }
          right={
            <AIControlPanel
              talentId={talentId}
              generating={generating}
              onGenerate={handleGenerate}
              notionDisabled={!selected}
              onPushNotion={handlePushNotion}
              pushing={pushing}
              onDelete={handleDelete}
              deleting={deleting}
              canDelete={!!selected}
              counselingMode={!!selected?.counselingMode}
              controls={controls}
              onControlsChange={setControls}
              onImported={handleImported}
            />
          }
        >
          {selected || liveHtml || liveLoading ? (
            <div className={styles.centerScroll}>
              {/* Live Notion badge */}
              {liveUrl && (
                <div className={styles.statusBar}>
                  <span className={styles.notionLiveBadge}>
                    ● {_('Live Notion page')}
                  </span>
                </div>
              )}

              {/* Center: Live Notion page OR AI-generated paper */}
              {liveHtml ? (
                <div className={styles.liveFrameWrap}>
                  <iframe
                    srcDoc={liveHtml}
                    className={styles.liveFrame}
                    sandbox="allow-same-origin"
                    title="Notion Live"
                  />
                </div>
              ) : liveLoading ? (
                <div className={styles.centerEmpty}>
                  <div className={styles.spinner} />
                  <div>{_('Loading Notion...')}</div>
                </div>
              ) : selected ? (
                <PortfolioPaper content={selected.content} />
              ) : null}
            </div>
          ) : (
            <div className={styles.centerEmpty}>
              <div className={styles.emptyText}>
                {_('Select a portfolio')}
              </div>
            </div>
          )}
        </StudioLayout>
      </div>
    </div>
  )
}
